import { Field } from "@ui/components/Field";
import { ChevronDownIcon } from "@ui/components/Icon/ChevronDownIcon";
import { dateSegmentClasses } from "@ui/components/StandardDateField/date-segment";
import { StandardRangeCalendar } from "@ui/components/StandardRangeCalendar";
import { tv } from "@ui/lib/tv";
import { Button } from "@ui/primitives/Button";
import {
  DateRangePicker,
  DateRangePickerDialog,
  DateRangePickerInput,
  DateRangePickerPopover,
  type DateRangePickerProps,
  DateRangePickerSegment,
} from "@ui/primitives/DateRangePicker";
import type { DateValue } from "@ui/primitives/shared";
import type { ReactNode } from "react";
import SeparatorSvg from "./separator.svg?react";
import TornPaperSvg from "./torn-paper.svg?react";

// Root/label/control/error chrome comes from Field; these are the bespoke
// range-picker bits (two inputs, torn-paper popover, toggle).
const standardDateRangePicker = tv({
  slots: {
    // The two inputs carry their own horizontal padding, so the Field control
    // drops its padding/gap and pins the standard 40px height.
    control: "h-10 gap-0 p-0",
    leadIcon: "shrink-0 pl-2 [&_svg]:size-6",
    input: "flex flex-1 items-center px-2",
    segment: dateSegmentClasses,
    separator: "shrink-0 self-stretch",
    toggle: [
      "flex h-full shrink-0 items-center justify-center px-2 text-neutral-400 outline-none",
      "hover:text-neutral-600",
      "data-[focus-visible]:text-neutral-600",
    ],
    popover: ["bg-transparent shadow-none", "drop-shadow-popover"],
    popoverInner: "relative min-h-[352px] w-[320px]",
    popoverBg: "pointer-events-none absolute inset-0 h-full w-full",
    dialog: "relative outline-none",
  },
});

export interface StandardDateRangePickerProps<T extends DateValue> extends DateRangePickerProps<T> {
  label: string;
  isInvalid?: boolean;
  errorMessage?: ReactNode;
  leadIcon?: ReactNode;
  /** Accessible name for the start date field. */
  startPlaceholder?: string;
  /** Accessible name for the end date field. */
  endPlaceholder?: string;
  /** Styles the field's outer box — see StandardTextField's `className`. */
  className?: string;
}

export function StandardDateRangePicker<T extends DateValue>({
  label,
  errorMessage,
  leadIcon,
  startPlaceholder = "Start date",
  endPlaceholder = "End date",
  className,
  ...props
}: StandardDateRangePickerProps<T>) {
  const styles = standardDateRangePicker();

  return (
    <Field className={className} isInvalid={props.isInvalid}>
      <DateRangePicker {...props} className="contents">
        <Field.Label>{label}</Field.Label>
        <Field.Control
          asGroup
          className={styles.control()}
          suffix={
            <Button className={styles.toggle()}>
              <ChevronDownIcon className="size-5" />
            </Button>
          }
        >
          {leadIcon && (
            <span aria-hidden="true" className={styles.leadIcon()}>
              {leadIcon}
            </span>
          )}
          <DateRangePickerInput aria-label={startPlaceholder} slot="start" className={styles.input()}>
            {(segment) => <DateRangePickerSegment segment={segment} className={styles.segment()} />}
          </DateRangePickerInput>
          <span aria-hidden="true" className={styles.separator()}>
            <SeparatorSvg className="h-full w-4" />
          </span>
          <DateRangePickerInput aria-label={endPlaceholder} slot="end" className={styles.input()}>
            {(segment) => <DateRangePickerSegment segment={segment} className={styles.segment()} />}
          </DateRangePickerInput>
        </Field.Control>
        {props.isInvalid && errorMessage && <Field.Error>{errorMessage}</Field.Error>}
        <DateRangePickerPopover className={styles.popover()}>
          <div className={styles.popoverInner()}>
            <TornPaperSvg aria-hidden="true" className={styles.popoverBg()} preserveAspectRatio="none" />
            <DateRangePickerDialog className={styles.dialog()}>
              <StandardRangeCalendar />
            </DateRangePickerDialog>
          </div>
        </DateRangePickerPopover>
      </DateRangePicker>
    </Field>
  );
}
